/** The quick actions every value on a booking surface offers — shared by the
 *  detail page header, the Booking tab, the grid's master-detail panel and the
 *  embedded delivery-log / package / family grids, so the same reference gets
 *  the same set everywhere.
 *
 *  Every href comes from gridLinks; every action from ValueActions' `act`.
 *  Id spaces (see gridLinks): the console booking pk is `gtu=` on /bookings and
 *  `participant=` on /delivery-logs and /import-runs; the operator's custom_id
 *  is `id=` on /bookings and `booking=` elsewhere.
 */
import type { DeliveryLog } from '@/api/bookings';
import { act, type ValueAction } from '@/components/ValueActions/ValueActions';
import type { FilterNode } from '@/components/QuerySearch';
import { externalUrl, looksLikeUrl } from '@/utils/externalUrl';
import {
  apiRequestsHref,
  bookingsHref,
  cond,
  deliveryLogsHref,
  idCond,
  importRunsHref,
  manifestConds,
  notificationsHref,
  ordersHref,
  sessionsHref,
  subscriptionsHref,
  usersHref,
} from '@/utils/gridLinks';

export interface BookingCtx {
  /** Scopes the grid links (`?tenant=`). */
  tenantId: number | null | undefined;
}

const tenantOf = (ctx: BookingCtx) => ctx.tenantId ?? null;

// ---- Identity -------------------------------------------------------------

export function tenantActions(tenant: { id: number }): ValueAction[] {
  return [
    act.open(`/tour-operators/${tenant.id}`, 'Open operator'),
    act.filter(bookingsHref([], tenant.id), 'Bookings of this operator'),
  ];
}

export function tourActions(tour: { id: number }, ctx: BookingCtx): ValueAction[] {
  const t = tenantOf(ctx);
  return [
    act.open(`/tours/${tour.id}`, 'Open tour'),
    act.open(`/tours/${tour.id}#departures`, 'Departures of this tour'),
    act.filter(bookingsHref([idCond('tour', tour.id)], t), 'Bookings on this tour'),
    act.filter(deliveryLogsHref([cond('tour', String(tour.id), 'exact')], { tenant: t }), 'Delivery logs for this tour'),
    act.filter(notificationsHref([cond('tour', String(tour.id), 'exact')], t), 'Notifications targeting this tour'),
  ];
}

/** The run: same tour, date and departure slot, booked only. */
export function departureActions(
  b: {
    tour: { id: number } | null;
    date: string | null;
    departure: { name: string | null } | null;
  },
  ctx: BookingCtx,
): ValueAction[] {
  const t = tenantOf(ctx);
  const out: ValueAction[] = [
    act.filter(
      bookingsHref(
        manifestConds({ tourId: b.tour?.id, date: b.date, departure: b.departure?.name }),
        t,
      ),
      'Bookings on this run (manifest)',
    ),
  ];
  if (b.departure?.name) {
    out.push(
      act.filter(
        deliveryLogsHref([cond('departure', b.departure.name)], { tenant: t }),
        'Delivery logs for this departure slot',
      ),
    );
  }
  return out;
}

export function dateActions(date: string, ctx: BookingCtx): ValueAction[] {
  return [act.filter(bookingsHref([cond('date', date, 'exact')], tenantOf(ctx)), 'Bookings on this date')];
}

/** The console booking pk. */
export function bookingIdActions(id: number, ctx: BookingCtx): ValueAction[] {
  const t = tenantOf(ctx);
  const pk = String(id);
  return [
    act.copy(pk, 'Copy booking id'),
    act.filter(bookingsHref([cond('gtu', pk)], t), 'This booking in the grid'),
    act.filter(deliveryLogsHref([cond('participant', pk, 'exact')], { tenant: t }), 'Delivery logs of this booking'),
    act.filter(importRunsHref([cond('participant', pk, 'exact')], t), 'Import runs that touched this booking'),
    act.filter(sessionsHref({ booking: id }), 'Sessions minted for this booking'),
  ];
}

export function stateActions(label: string, ctx: BookingCtx): ValueAction[] {
  return [act.filter(bookingsHref([cond('state', label)], tenantOf(ctx)), `Bookings in state "${label}"`)];
}

// ---- Guest ----------------------------------------------------------------

export function guestActions(guest: { id: number }, ctx: BookingCtx): ValueAction[] {
  const t = tenantOf(ctx);
  return [
    act.open(`/users/${guest.id}`, 'Open user'),
    act.filter(bookingsHref([idCond('user', guest.id)], t), 'Bookings of this user'),
    act.filter(ordersHref([idCond('user', guest.id)], t), 'Orders of this user'),
    act.filter(sessionsHref({ user: guest.id }), 'Sessions of this user'),
  ];
}

export function emailActions(email: string, ctx: BookingCtx): ValueAction[] {
  const t = tenantOf(ctx);
  return [
    act.mailto(email),
    act.copy(email, 'Copy email'),
    act.filter(bookingsHref([cond('email', email)], t), 'Bookings with this email'),
    act.filter(deliveryLogsHref([cond('email', email)], { tenant: t }), 'Delivery logs to this email'),
    act.filter(usersHref('guests', { tenant: t, email_contains: email }), 'Guest accounts with this email'),
    act.filter(importRunsHref([cond('user', email, 'exact')], t), 'Import runs for this email'),
  ];
}

export function phoneActions(phone: string, ctx: BookingCtx): ValueAction[] {
  return [
    act.tel(phone),
    act.copy(phone, 'Copy phone'),
    act.filter(bookingsHref([cond('phone', phone)], tenantOf(ctx)), 'Bookings with this phone'),
  ];
}

/** One of the guest's contacts (the Guest panel's contacts list): an email or a phone as
 *  above; a URL opens (a scheme-less one as https) and copies. */
export function contactActions(type: 'email' | 'phone' | 'url', value: string, ctx: BookingCtx): ValueAction[] {
  if (type === 'email') return emailActions(value, ctx);
  if (type === 'phone') return phoneActions(value, ctx);
  return [...(looksLikeUrl(value) ? [act.external(externalUrl(value), 'Open link')] : []), act.copy(value, 'Copy URL')];
}

export function registrationMethodActions(label: string, ctx: BookingCtx): ValueAction[] {
  return [act.filter(bookingsHref([cond('create_source', label)], tenantOf(ctx)), `Bookings registered via "${label}"`)];
}

// ---- References -----------------------------------------------------------

export function orderActions(
  b: { order_id: number | null; order_ref: string | null },
  ctx: BookingCtx,
): ValueAction[] {
  const t = tenantOf(ctx);
  const out: ValueAction[] = [];
  if (b.order_id) out.push(act.open(`/orders/${b.order_id}`, 'Open order'));
  if (b.order_ref) {
    const ref = b.order_ref;
    out.push(
      act.copy(ref, 'Copy order ref'),
      act.filter(bookingsHref([cond('order', ref)], t), 'Bookings in this order'),
      act.filter(ordersHref([cond('order', ref)], t), 'This order in the grid'),
      act.filter(deliveryLogsHref([cond('order', ref)], { tenant: t }), 'Delivery logs for this order'),
      act.filter(importRunsHref([cond('order', ref, 'exact')], t), 'Import runs for this order'),
    );
  }
  return out;
}

/** The operator's own id for the booking (`custom_id`). */
export function customIdActions(customId: string, ctx: BookingCtx): ValueAction[] {
  const t = tenantOf(ctx);
  return [
    act.copy(customId, 'Copy custom id'),
    act.filter(bookingsHref([cond('id', customId)], t), 'Bookings with this custom id'),
    act.filter(deliveryLogsHref([cond('booking', customId)], { tenant: t }), 'Delivery logs for this booking id'),
    act.filter(apiRequestsHref([cond('booking', customId, 'exact')], { tenant: t }), 'API requests for this booking id'),
    act.filter(importRunsHref([cond('booking', customId, 'exact')], t), 'Import runs for this booking id'),
  ];
}

/** The booking's second id in its booking system (`alt_custom_id` — Ventrata's native id
 *  while `custom_id` holds its uuid). The bookings `id` and the log grids' `booking`
 *  searches match it too, so it gets the same links as the custom id. */
export function altCustomIdActions(altId: string, ctx: BookingCtx): ValueAction[] {
  const t = tenantOf(ctx);
  return [
    act.copy(altId, 'Copy alt id'),
    act.filter(bookingsHref([cond('id', altId)], t), 'Bookings with this alt id'),
    act.filter(deliveryLogsHref([cond('booking', altId)], { tenant: t }), 'Delivery logs for this booking id'),
    act.filter(apiRequestsHref([cond('booking', altId, 'exact')], { tenant: t }), 'API requests for this booking id'),
    act.filter(importRunsHref([cond('booking', altId, 'exact')], t), 'Import runs for this booking id'),
  ];
}

export function refActions(kind: 'supplier' | 'reseller', value: string, ctx: BookingCtx): ValueAction[] {
  const t = tenantOf(ctx);
  const label = kind === 'supplier' ? 'supplier' : 'reseller';
  return [
    act.copy(value, `Copy ${label} ref`),
    act.filter(bookingsHref([cond(kind, value)], t), `Bookings with this ${label} ref`),
    act.filter(ordersHref([cond(kind, value)], t), `Orders with this ${label} ref`),
    act.filter(deliveryLogsHref([cond(kind, value)], { tenant: t }), `Delivery logs with this ${label} ref`),
    act.filter(importRunsHref([cond(kind, value, 'exact')], t), `Import runs with this ${label} ref`),
  ];
}

export function affiliateActions(value: string, ctx: BookingCtx): ValueAction[] {
  return [
    act.copy(value, 'Copy affiliate id'),
    act.filter(bookingsHref([cond('affiliate', value)], tenantOf(ctx)), 'Bookings with this affiliate id'),
  ];
}

/** The departure INSTANCE code (not the time-of-day tag). */
export function departureKeyActions(value: string, ctx: BookingCtx): ValueAction[] {
  return [
    act.copy(value, 'Copy departure code'),
    act.filter(bookingsHref([cond('departure_key', value)], tenantOf(ctx)), 'Bookings with this departure code'),
  ];
}

export function parentCustomIdActions(value: string, ctx: BookingCtx): ValueAction[] {
  return [
    act.copy(value, 'Copy parent custom id'),
    act.filter(bookingsHref([cond('id', value)], tenantOf(ctx)), 'The parent booking in the grid'),
  ];
}

// ---- Provenance -----------------------------------------------------------

export function integrationActions(codename: string, ctx: BookingCtx): ValueAction[] {
  const t = tenantOf(ctx);
  const out: ValueAction[] = [
    act.filter(bookingsHref([cond('integration', codename)], t), 'Bookings from this integration'),
    act.filter(apiRequestsHref([cond('integration', codename)], { tenant: t }), 'API requests of this integration (7 days)'),
    act.filter(subscriptionsHref([cond('restech', codename)], t), 'Webhook subscriptions of this integration'),
  ];
  if (t != null) out.push(act.open(`/tour-operators/${t}/integrations`, "Operator's integrations"));
  return out;
}

export function restechAccountActions(slug: string, ctx: BookingCtx): ValueAction[] {
  const t = tenantOf(ctx);
  const out: ValueAction[] = [
    act.copy(slug, 'Copy account slug'),
    act.filter(subscriptionsHref([cond('account', slug)], t), 'Webhook subscriptions of this account'),
  ];
  if (t != null) out.push(act.open(`/tour-operators/${t}/integrations`, "Operator's integrations"));
  return out;
}

export function bookingSystemActions(label: string, ctx: BookingCtx): ValueAction[] {
  return [act.filter(bookingsHref([cond('booking_system', label)], tenantOf(ctx)), `Bookings from "${label}"`)];
}

export function surveyActions(label: string, ctx: BookingCtx): ValueAction[] {
  return [act.filter(bookingsHref([cond('survey', label)], tenantOf(ctx)), `Bookings with survey "${label}"`)];
}

export function paxActions(n: number, ctx: BookingCtx): ValueAction[] {
  return [act.filter(bookingsHref([cond('pax', String(n), 'exact')], tenantOf(ctx)), `Bookings with ${n} people`)];
}

/** Created/updated: the ±delta presets on the bookings grid, plus the
 *  provenance source label as its own filter. */
export function timestampActions(
  ns: 'created' | 'updated',
  iso: string,
  source: string | null | undefined,
  ctx: BookingCtx,
): ValueAction[] {
  const t = tenantOf(ctx);
  const out: ValueAction[] = act.timeRanges(iso, (range) => bookingsHref([range], t), ns);
  if (source) {
    const sourceNs = ns === 'created' ? 'create_source' : 'update_source';
    out.push(act.filter(bookingsHref([cond(sourceNs, source)], t), `Bookings ${ns} via "${source}"`));
  }
  return out;
}

// ---- Embedded delivery-log rows ------------------------------------------

export interface DeliveryLogCtx extends BookingCtx {
  bookingId: number;
}

const participantCond = (ctx: DeliveryLogCtx): FilterNode =>
  cond('participant', String(ctx.bookingId), 'exact');

export function deliveryLogIdActions(log: DeliveryLog): ValueAction[] {
  return [act.open(`/delivery-logs/${log.id}`, 'Open delivery log'), act.copy(log.id, 'Copy log uuid')];
}

export function deliveryLogEventActions(event: string, ctx: DeliveryLogCtx): ValueAction[] {
  const t = tenantOf(ctx);
  return [
    act.filter(
      deliveryLogsHref([cond('event', event), participantCond(ctx)], { tenant: t }),
      'Same event on this booking',
    ),
    act.filter(deliveryLogsHref([cond('event', event)], { tenant: t }), 'Same event, all bookings (7 days)'),
    act.filter(notificationsHref([cond('event', event)], t), 'Notifications for this event'),
    act.copy(event, 'Copy event code'),
  ];
}

export function deliveryLogLogIdActions(
  log: { log_id: string | null; log_tag: string | null },
  ctx: DeliveryLogCtx,
): ValueAction[] {
  const t = tenantOf(ctx);
  const out: ValueAction[] = [];
  if (log.log_id) {
    out.push(
      act.copy(log.log_id, 'Copy log id'),
      act.filter(deliveryLogsHref([cond('log', log.log_id)], { tenant: t }), 'Every attempt with this log id'),
    );
  }
  if (log.log_tag) {
    out.push(act.filter(deliveryLogsHref([cond('tag', log.log_tag)], { tenant: t }), 'Same redelivery run tag'));
  }
  return out;
}

export function deliveryLogEmittedActions(iso: string, ctx: DeliveryLogCtx): ValueAction[] {
  const t = tenantOf(ctx);
  return act.timeRanges(iso, (range) => deliveryLogsHref([participantCond(ctx), range], { tenant: t }));
}

export function deliveryLogStatusActions(label: string, ctx: DeliveryLogCtx): ValueAction[] {
  const t = tenantOf(ctx);
  return [
    act.filter(
      deliveryLogsHref([cond('status', label), participantCond(ctx)], { tenant: t }),
      `"${label}" logs on this booking`,
    ),
    act.filter(deliveryLogsHref([cond('status', label)], { tenant: t }), `"${label}" logs, all bookings (7 days)`),
  ];
}

export function deliveryLogNotificationActions(notificationId: number, ctx: DeliveryLogCtx): ValueAction[] {
  const t = tenantOf(ctx);
  const nid = cond('notification', String(notificationId), 'exact');
  return [
    act.open(`/notifications/${notificationId}`, 'Open notification'),
    act.filter(deliveryLogsHref([nid, participantCond(ctx)], { tenant: t }), 'This notification on this booking'),
    act.filter(deliveryLogsHref([nid], { tenant: t }), 'This notification, all bookings (7 days)'),
  ];
}

/** The whole embedded list, on the real grid. */
export function deliveryLogsOfBookingHref(ctx: DeliveryLogCtx): string {
  return deliveryLogsHref([participantCond(ctx)], { tenant: tenantOf(ctx) });
}
